import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { ValidatorErrorPipe } from '../../pipes/validator-error';
import type { AddMarkerDialogData } from '../../../features/staff-monitor/model/add-marker-dialog-data';
import { FIELD_VALIDATORS } from '../../../app.constants';
import { A11yModule } from '@angular/cdk/a11y';
import { MarkerService } from './marker.service';
import type { MarkerPostDTO } from '../../../features/staff-monitor/model/marker-post.dto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { normalizeFormControls } from '../../utils/normalize-form-controls';
import { MatSelectModule } from '@angular/material/select';
import { isNil } from '@pct/ts-typeguard';
import { ServerTimeService } from '../../../core/server-time.service';
import { addDeltaToTimeInZone } from '../../utils/time-converter';

@Component({
  selector: 'app-add-marker-dialog',
  imports: [
    TranslocoDirective,
    MatDialogTitle,
    MatButton,
    MatDialogActions,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ValidatorErrorPipe,
    MatHint,
    A11yModule,
    MatSelectModule,
  ],
  providers: [MarkerService],
  templateUrl: './add-marker-dialog.component.html',
  styleUrl: './add-marker-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class AddMarkerDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<AddMarkerDialogComponent, boolean>>(MatDialogRef);
  protected readonly data = inject<AddMarkerDialogData>(MAT_DIALOG_DATA);
  private readonly markerService = inject(MarkerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly serverTimeService = inject(ServerTimeService);

  protected readonly messageMaxLength = FIELD_VALIDATORS.DESCRIPTION_MAX_LENGTH;

  protected readonly deltaTimeControl = new FormControl(
    {
      value: addDeltaToTimeInZone(
        this.data.recordingStartedAt,
        this.data.delta,
        this.serverTimeService.serverTimeZone(),
      ),
      disabled: true,
    },
    { validators: [Validators.required], nonNullable: true },
  );

  protected readonly participantControl = new FormControl<string[]>([this.data.deviceId], {
    validators: [Validators.required],
    nonNullable: true,
  });

  protected readonly messageControl = new FormControl('', {
    validators: [Validators.maxLength(this.messageMaxLength)],
    nonNullable: true,
  });

  protected readonly form = new FormGroup({
    message: this.messageControl,
    delta: this.deltaTimeControl,
    participant: this.participantControl,
  });

  protected save(): void {
    normalizeFormControls(this.messageControl);
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const participants = this.data.participants
      .filter(p => this.participantControl.value.includes(p.mindDeviceId))
      .map(p => ({
        assistId: p.assistId,
        deviceId: p.mindDeviceId,
      }));

    // adjuk-e hozzá meglévő manual group-hoz?
    const groupId = this.data.markers?.find(
      m => m.type === 'Manual' && this.data.delta === m.delta && this.messageControl.value === m.message,
    )?.groupId;

    const markerData: MarkerPostDTO = {
      delta: this.data.delta,
      message: this.messageControl.value,
      participants,
      ...(!isNil(groupId) && { groupId }),
    };

    this.markerService
      .addMarker(this.data.recordingId, markerData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close(true));
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
